import { useEffect, useSyncExternalStore } from 'react';

import {
  type EditorSaveCoordinator,
  type ShiftDraftAutosaveController,
  type ShiftDraftAutosaveSnapshot,
} from './shift-draft-autosave';

export interface UseShiftDraftAutosaveOptions {
  readonly saveCoordinator?: EditorSaveCoordinator;
}

/**
 * Connect a controller to React, page lifecycle flushes and the PWA update gate.
 * The caller owns the controller and must dispose it when its editor lifetime
 * ends; this hook only attaches and removes browser listeners.
 */
export function useShiftDraftAutosave(
  controller: ShiftDraftAutosaveController,
  options: UseShiftDraftAutosaveOptions = {},
): ShiftDraftAutosaveSnapshot {
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );

  useEffect(() => {
    const handleBlur = () => controller.handleBlur();
    const handlePageHide = () => controller.handleBlur();
    const handleVisibilityChange = () =>
      controller.handleVisibilityChange(document.visibilityState === 'hidden');
    const unregisterSave = options.saveCoordinator?.register(() => controller.saveNow());

    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      unregisterSave?.();
    };
  }, [controller, options.saveCoordinator]);

  return snapshot;
}
